import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, ControllerRenderProps, useForm, useWatch } from 'react-hook-form';
import {
  DashboardCommerciauxFormSchema,
  RegisterUserStepMutation,
  RegisterUserStepMutationVariables,
} from './DashboardCommerciauxUpdateForm.types';
import { useMutation, useQueryClient } from 'react-query';
import departements from '../../../../../../../assets/json/departements-region.json';
import { updateCommercialStepData } from './DashboardCommerciauxUpdateForm.data';
import { getUpdateCommercialSchema } from './DashboardCommerciauxUpdateForm.schema';
import {
  UPDATE_COMMERCIAL_QUERY_KEY,
  updateCommercialQuery,
} from './DashboardCommerciauxUpdateForm.query';
import { useNavigate } from 'react-router-dom';
import SearchInput from '../../../../../../commons/SearchInput/SearchInput';
import { useAddressAutocomplete } from '../../../../../../../hooks/fetchers/useAddressAutocomplete/useAddressAutocomplete';
import { ChangeEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  AddressSuggestion,
  AddressTypes,
} from '../../../../../../../types/models/address/address.types';
import Input from '../../../../../../commons/Input/Input';
import Button from '../../../../../../designSystem/Button/Button';
import { ButtonSize } from '../../../../../../designSystem/Button/Button.types';
import { extractNumberFromString } from '../../../../../../../utils/helpers/numbers/extractNumberFromString';
import { DashboardCommerciauxContext } from '../../DashboardCommerciaux.context';
import { FormItem } from '../../../../../../commons/Form/Form.types';
import Select from '../../../../../../commons/Select/Select';
import { CommercialTypes } from '../../../../../../../types/api/endpoints/commercial/commercial.types';

export default () => {
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const { selectedCOM } = useContext(DashboardCommerciauxContext);

  const [searchInputValue, setSearchInputValue] = useState<string>('');

  const { data } = useAddressAutocomplete(searchInputValue, { types: AddressTypes.City });

  const formProps = useForm<DashboardCommerciauxFormSchema>({
    resolver: yupResolver(getUpdateCommercialSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    setValue,
    formState: { isValid, errors },
  } = formProps;

  const type = useWatch({
    control,
    name: 'type'
  });

  useEffect(() => {
    if (selectedCOM) {
      setValue('firstName', selectedCOM.firstName, { shouldValidate: true });
      setValue('lastName', selectedCOM.lastName, { shouldValidate: true });
      setValue('type', selectedCOM.type, { shouldValidate: true });
      setValue('email', selectedCOM.email, { shouldValidate: true });
      setValue('phone', selectedCOM.phone, { shouldValidate: true });
      setValue('siret', selectedCOM.siret, { shouldValidate: true });
      setValue('city', selectedCOM.city, { shouldValidate: true });
      setValue('department', selectedCOM.department, { shouldValidate: true });
      setValue('region', selectedCOM.region, { shouldValidate: true });
      if (selectedCOM.tva) {
        setValue('tva', selectedCOM.tva, { shouldValidate: true });
      }
    }
  }, [selectedCOM]);

  const { mutate } = useMutation<
    Awaited<ReturnType<RegisterUserStepMutation>>,
    Error,
    RegisterUserStepMutationVariables
  >((data) => updateCommercialQuery(data), {
    mutationKey: UPDATE_COMMERCIAL_QUERY_KEY,
    onMutate: () => queryClient.cancelMutations(),
    onSuccess: () => {
      navigate(0);
    },
    onError: (error) => {
      alert(error.message);
    },
  });

  const onAddressChange = (
    e: ChangeEvent<HTMLInputElement>,
    onChange: (e: ChangeEvent<HTMLInputElement>) => void
  ) => {
    const value = e.target.value;
    onChange(e);
    setSearchInputValue(value);
  };

  const handleSelectedSuggestion = (suggestion: AddressSuggestion) => {
    setSearchInputValue('');
    setValue('city', suggestion.city || suggestion.address, { shouldValidate: true });
    setValue(
      'department',
      departements.find(
        (res) =>
          res.num_dep.toString() == suggestion.department_code ||
          (suggestion.short_code && res.num_dep == extractNumberFromString(suggestion.short_code))
      )?.dep_name || ''
    ),
      {
        shouldValidate: true,
      };
    setValue(
      'region',
      departements.find(
        (res) =>
          res.num_dep.toString() == suggestion.department_code ||
          // @ts-ignore
          res.num_dep.toString() == suggestion.region_code ||
          // @ts-ignore
          (suggestion.short_code && res.num_dep == extractNumberFromString(suggestion.short_code))
      )?.region_name || '',
      {
        shouldValidate: true,
      }
    );
  };

  const updatedFormData = useMemo(
    () =>
    updateCommercialStepData.map((row) =>
        row.map((item) => {
          switch (item.name) {
            case 'type':
              return {
                ...item,
                defaultValue: {
                  value: selectedCOM?.type,
                  labrl: selectedCOM?.type
                }
              };
            default:
              return item;
          }
        })
      ),
    [selectedCOM]
  );

  const AdaptedFormField = useCallback(
    (props: FormItem<DashboardCommerciauxFormSchema> & ControllerRenderProps<DashboardCommerciauxFormSchema>) => {
      const { name, placeholder, label, type, options, value, onChange, onChangeValue, ...rest } = props
      switch (props.name) {
        case 'city':
          return (
            <SearchInput
              suggestions={data}
              placeholder={placeholder || ''}
              type={type}
              onChange={(e) => onAddressChange(e, onChange)}
              error={errors[name]?.message as string}
              {...{ name, value, label, handleSelectedSuggestion }}
              {...rest}
            />
          )
        case 'type':
          return (
            <Select
              placeholder={placeholder || ''}
              error={errors[name]?.message as string}
              {...{ label, options, ...rest }}
              value={{
                value: value as string,
                label: value as string
              }}
              // {...props}
              // @ts-ignore
              onChange={({ value }: SelectOption) => {
                onChange(value);
                if (onChangeValue) {
                  onChangeValue(value);
                }
              }}
            />
          )
        default:
          return (
            <Input
              placeholder={placeholder || ''}
              type={type}
              disabled={Boolean(['department', 'region'].includes(name))}
              error={errors[name]?.message as string}
              {...{ name, onChange, value, label }}
              {...rest}
            />
          )
      }
    },
    [onAddressChange]
  )

  const onSubmit = handleSubmit((data) => mutate({ ...data, id: selectedCOM?.id || 0 }));

  return (
    <form className="form" style={{ overflowY: 'auto' }}>
      {updatedFormData.map((row, rowIndex) => (
        <div key={rowIndex} className="form__row">
          {row.map((input, inputIndex) => (
            <Controller
              key={inputIndex}
              name={input.name}
              render={(renderProps) => {
                if (type === CommercialTypes.Employee && ['siret', 'tva'].includes(input.name)) {
                  return <></>
                } else {
                  return (
                    // @ts-ignore
                    <AdaptedFormField 
                      {...input} 
                      {...renderProps} 
                      onChange={renderProps.field.onChange} 
                      value={renderProps.field.value}
                    />
                  )
                }
              }}
              {...{ control }}
            />
          ))}
        </div>
      ))}
      <Button
        style={{ alignSelf: 'unset' }}
        onClick={(e) => {
          e.preventDefault();
          onSubmit();
        }}
        size={ButtonSize.Large}
        disabled={!isValid}>
        Valider
      </Button>
    </form>
  );
};
